import type { JSX } from 'react';
import { FormGroup } from 'react-bootstrap';
import Select, { OnChangeValue, OptionProps, Props, components } from 'react-select';

import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import styles from 'Components/FormField/FormField.module.css';
import { FormLabel } from 'Components/FormField/FormLabel/FormLabel';
import { SELECT_MENU_Z_INDEX } from 'Components/FormField/SelectComponentsZIndex';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { getUserNameFromUser, sortFormattedUsers } from 'Helpers/UserUtils';
import { ColorTheme } from 'Models/Types/GlobalType';
import { Status, UserResponse } from 'Models/User';

import { UserOption, UserOptionProps } from './UserOption/UserOption';

/**
 * @param colorTheme The type of background that the component will be displayed against. If not supplied, `'light'` will be used.
 * @param formFieldLabel The label to display above the field to explain its purpose to the user.
 * @param notClearable If true, the select field cannot be cleared. If falsy, the select field has an icon to clear the selection
 */
interface FormFieldUserSelectProps {
    colorTheme?: ColorTheme;
    formFieldId: string;
    formFieldLabel?: string;
    required?: boolean;
    tooltip?: string;
    notClearable?: boolean;
    onUserSelected: (value: UserResponse | undefined, formFieldId: string) => void;
    selectedUser?: UserResponse;
    disabled?: boolean;
    includeInactive?: boolean;
    /**
     * If defined, the component will only show users with the specified roles. If not defined, all users will be shown.
     */
    rolesRestriction?: Role[];
}

class UserResponseOption {
    value: string;
    label: string;
    user: UserResponse;

    constructor(user: UserResponse) {
        this.value = user.cognito_subject;
        this.label = getUserNameFromUser(user);
        this.user = user;
    }
}

export const FormFieldUserSelect = (props: FormFieldUserSelectProps) => {
    const cachedUsers = useCachedData().users;
    const { rolesRestriction } = props;

    const colorTheme = props.colorTheme ?? 'light';

    const mapUserToUserOption = (user: UserResponse): UserResponseOption => new UserResponseOption(user);

    const filterAndFormatUsers = (): UserResponseOption[] => {
        let users = [...cachedUsers];

        if (rolesRestriction !== undefined) {
            users = users.filter((user) => userHasAuthorizedRole(user.roles, rolesRestriction));
        }

        if (!props.includeInactive) {
            users = users.filter((user) => user.status === Status.ACTIVE);
        }

        return sortFormattedUsers(users).map(mapUserToUserOption);
    };

    const handleChange = (value: OnChangeValue<UserResponseOption, false>): void => {
        if (value && value instanceof UserResponseOption) {
            props.onUserSelected(value.user, props.formFieldId);
        } else if (value === undefined || value === null) {
            props.onUserSelected(undefined, props.formFieldId);
        }
    };

    const userOption = (optionProps: OptionProps<UserResponseOption, false>): JSX.Element => {
        const userOptionProps: UserOptionProps = {
            user: optionProps.data.user,
        };
        return (
            <components.Option {...optionProps}>
                <UserOption {...userOptionProps} />
            </components.Option>
        );
    };

    const selectProps: Props<UserResponseOption, false> = {
        isClearable: props.notClearable === true ? false : true,
        options: filterAndFormatUsers(),
        onChange: handleChange,
        value: props.selectedUser ? mapUserToUserOption(props.selectedUser) : undefined,
        isDisabled: props.disabled,
        inputId: props.formFieldId,
    };

    return (
        <FormGroup controlId={props.formFieldId} className={styles.formField}>
            {props.formFieldLabel && (
                <FormLabel color={colorTheme === 'light' ? undefined : 'white'} required={props.required}>
                    {props.formFieldLabel}
                </FormLabel>
            )}
            {props.tooltip && <FormFieldTooltip text={props.tooltip} />}
            <Select
                {...selectProps}
                components={{ Option: userOption }}
                theme={(theme) => ({
                    ...theme,
                    borderRadius: 9,
                    colors: {
                        ...theme.colors,
                        primary25: '#05263717',
                        text: 'var(--hps-blue)',
                        primary: colorTheme === 'light' ? 'var(--hps-blue)' : 'transparent',
                    },
                })}
                styles={{
                    ...styles,
                    menu: (base) => ({
                        ...base,
                        zIndex: SELECT_MENU_Z_INDEX,
                    }),
                    control: (base) => ({
                        ...base,
                        backgroundColor: colorTheme === 'light' ? base.backgroundColor : 'var(--hps-blue)',
                        borderColor: '#053c591a',
                        color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-white)',
                    }),
                    dropdownIndicator: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? base.color : 'var(--hps-white)',
                    }),
                    input: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-white)',
                    }),
                    multiValueRemove: (styles, { data }) => ({
                        ...styles,
                        ':hover': {
                            color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-dark-blue)',
                        },
                    }),
                    option: (styles, { isSelected }) => ({
                        ...styles,
                        backgroundColor: isSelected ? '#05263717' : '',
                        color: isSelected ? 'var(--hps-blue)' : '',
                    }),
                    placeholder: (base) => ({
                        ...base,
                        color: colorTheme === 'light' ? base.color : 'var(--hps-white)',
                    }),
                    singleValue: (styles) => ({
                        ...styles,
                        color: colorTheme === 'light' ? 'var(--hps-blue)' : 'var(--hps-white)',
                    }),
                }}
            />
        </FormGroup>
    );
};
